import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  HeadingLevel,
  ShadingType,
  PageNumber,
  Header,
  Footer,
  ImageRun,
} from 'docx';
import type { Report, ReportComment, ReportMedia } from '../services/reportService';
import { getLocale, t, tn } from '../i18n';
import {
  buildReportSections,
  getReportTitle,
  reportTypeLabelFor,
} from './reportPresentation';
import { getReportShiftLabel } from '../services/reportService';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const ACCENT_R = 30;
const ACCENT_G = 58;
const ACCENT_B = 95;

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return t('N/A');
  return new Date(dateStr).toLocaleString(getLocale());
}

function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return t('N/A');
  return new Date(dateStr).toLocaleDateString(getLocale());
}

function getStatusLabel(status: string | null | undefined): string {
  const map: Record<string, string> = {
    submitted: t('Submitted'),
    in_review: t('In Review'),
    needs_more_info: t('Needs More Info'),
    escalated: t('Escalated'),
    closed: t('Closed'),
  };
  return (status && map[status]) || status || t('Unknown');
}

async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function fetchImageAsArrayBuffer(url: string): Promise<ArrayBuffer | null> {
  try {
    const response = await fetch(url);
    return await response.arrayBuffer();
  } catch {
    return null;
  }
}

function getImageType(url: string): 'image/jpeg' | 'image/png' | 'image/gif' {
  const lower = url.toLowerCase();
  if (lower.includes('.png')) return 'image/png';
  if (lower.includes('.gif')) return 'image/gif';
  return 'image/jpeg';
}

// ─── PDF Export ───────────────────────────────────────────────────────────────

export async function exportReportToPdf(
  report: Report,
  comments: ReportComment[],
  media: ReportMedia[],
  includeInternalComments = false,
  companyName = '',
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = 0;

  const profiles = report['profiles'] as { full_name?: string | null } | null;
  const sites = report['sites'] as { name?: string | null } | null;
  const assignee = report['assignee'] as { full_name?: string | null } | null;
  const title = getReportTitle(report);
  const reportType = reportTypeLabelFor(report.report_type);

  // ── Header band ──────────────────────────────────────────────────────────
  doc.setFillColor(ACCENT_R, ACCENT_G, ACCENT_B);
  doc.rect(0, 0, pageW, 42, 'F');

  doc.setTextColor(255, 255, 255);
  const companyLabel = companyName.toUpperCase() || t('SECURITY REPORT');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(companyLabel, margin, 12);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(title, contentW - 40);
  doc.text(titleLines, margin, 22);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(reportType, margin, 22 + titleLines.length * 7 + 2);

  // Report ID top-right
  doc.setFontSize(8);
  doc.text(`ID: ${report.id.slice(0, 8).toUpperCase()}`, pageW - margin, 12, { align: 'right' });
  doc.text(t('Exported: {date}', { date: new Date().toLocaleDateString(getLocale()) }), pageW - margin, 18, { align: 'right' });

  y = 52;

  // ── Metadata table ────────────────────────────────────────────────────────
  const meta: string[][] = [
    [t('Guard'), profiles?.full_name || t('Unknown')],
    [t('Site'), sites?.name || t('Unknown')],
    [t('Shift'), getReportShiftLabel(report)],
    [t('Status'), getStatusLabel(report.status)],
    [t('Priority'), (report.priority || t('N/A')).toUpperCase()],
    [t('Assigned To'), assignee?.full_name || t('Unassigned')],
    [t('Submitted'), formatDate(report.created_at)],
  ];
  if (report.status_updated_at && report.status_updated_at !== report.created_at) {
    meta.push([t('Last Updated'), formatDate(report.status_updated_at)]);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    body: meta,
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40, fillColor: [245, 247, 250], textColor: [60, 70, 90] },
      1: { textColor: [30, 30, 50] },
    },
    styles: { fontSize: 10, cellPadding: 3.5, lineColor: [220, 225, 235], lineWidth: 0.2 },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    theme: 'grid',
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;

  // ── Report sections ───────────────────────────────────────────────────────
  const sections = buildReportSections(report);
  for (const section of sections) {
    if (y > pageH - 40) { doc.addPage(); y = 20; }

    // Section heading
    doc.setFillColor(240, 244, 250);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(ACCENT_R, ACCENT_G, ACCENT_B);
    doc.text(section.title, margin + 3, y + 5.5);
    y += 10;

    if (section.facts.length > 0) {
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        body: section.facts.map((f) => [f.label, f.value]),
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 55, fillColor: [250, 251, 253], textColor: [70, 80, 100] },
          1: { textColor: [30, 30, 50] },
        },
        styles: { fontSize: 10, cellPadding: 3, lineColor: [225, 230, 240], lineWidth: 0.15 },
        theme: 'grid',
      });
      y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
    }

    if (section.body) {
      if (y > pageH - 30) { doc.addPage(); y = 20; }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 45, 65);
      const bodyLines = doc.splitTextToSize(section.body, contentW);
      doc.text(bodyLines, margin, y);
      y += bodyLines.length * 5 + 6;
    }

    y += 4;
  }

  // ── Source content ────────────────────────────────────────────────────────
  if (report.content) {
    if (y > pageH - 40) { doc.addPage(); y = 20; }
    doc.setFillColor(240, 244, 250);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(ACCENT_R, ACCENT_G, ACCENT_B);
    doc.text(sections.length > 0 ? t('Source Summary') : t('Report Content'), margin + 3, y + 5.5);
    y += 12;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 45, 65);
    const contentLines = doc.splitTextToSize(report.content, contentW);
    doc.text(contentLines, margin, y);
    y += contentLines.length * 5 + 10;
  }

  // ── Attachments ───────────────────────────────────────────────────────────
  const imageMedia = media.filter((m) => m.media_type !== 'video' && m.url);
  const videoMedia = media.filter((m) => m.media_type === 'video');

  if (media.length > 0) {
    if (y > pageH - 40) { doc.addPage(); y = 20; }
    doc.setFillColor(240, 244, 250);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(ACCENT_R, ACCENT_G, ACCENT_B);
    doc.text(t('Attachments ({count})', { count: media.length }), margin + 3, y + 5.5);
    y += 13;

    // Embed images (max 3 per row)
    if (imageMedia.length > 0) {
      const imgW = (contentW - 8) / Math.min(imageMedia.length, 3);
      const imgH = 50;
      let col = 0;
      let rowStartY = y;

      for (const m of imageMedia) {
        if (col === 3) {
          col = 0;
          rowStartY = y;
        }
        if (col === 0 && y > pageH - imgH - 20) {
          doc.addPage();
          y = 20;
          rowStartY = y;
        }

        const b64 = await fetchImageAsBase64(m.url!);
        if (b64) {
          try {
            const ext = b64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
            const x = margin + col * (imgW + 4);
            doc.addImage(b64, ext, x, rowStartY, imgW, imgH);
            // Caption
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 110, 130);
            doc.text(t('Photo'), x + imgW / 2, rowStartY + imgH + 4, { align: 'center' });
          } catch {
            // skip unembeddable image
          }
        }
        col++;
        if (col === Math.min(imageMedia.length, 3)) {
          y = rowStartY + imgH + 10;
          col = 0;
          rowStartY = y;
        }
      }
      if (col > 0) {
        y = rowStartY + imgH + 10;
      }
    }

    // List video attachments
    if (videoMedia.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(80, 90, 110);
      doc.text(tn(
        '{count} video attachment (view in Guardian portal)',
        '{count} video attachments (view in Guardian portal)',
        videoMedia.length,
      ), margin, y);
      y += 8;
    }
  }

  // ── Comments ──────────────────────────────────────────────────────────────
  const exportableComments = includeInternalComments
    ? comments
    : comments.filter((c) => !c.is_internal);

  if (exportableComments.length > 0) {
    if (y > pageH - 40) { doc.addPage(); y = 20; }
    doc.setFillColor(240, 244, 250);
    doc.rect(margin, y, contentW, 8, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(ACCENT_R, ACCENT_G, ACCENT_B);
    doc.text(t('Comments ({count})', { count: exportableComments.length }), margin + 3, y + 5.5);
    y += 13;

    for (const comment of exportableComments) {
      if (y > pageH - 30) { doc.addPage(); y = 20; }

      // Author line
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(50, 60, 90);
      const author = comment.profiles?.full_name || t('Unknown');
      const commentDate = formatDate(comment.created_at);
      const internalTag = comment.is_internal ? ` [${t('Internal')}]` : '';
      doc.text(`${author}${internalTag}  ·  ${commentDate}`, margin, y);
      y += 5.5;

      // Body
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 45, 65);
      const bodyLines = doc.splitTextToSize(comment.body, contentW - 6);
      if (comment.is_internal) {
        doc.setFillColor(255, 251, 240);
        doc.rect(margin, y - 2, contentW, bodyLines.length * 5 + 5, 'F');
      }
      doc.text(bodyLines, margin + 3, y + 1.5);
      y += bodyLines.length * 5 + 8;

      // Divider
      doc.setDrawColor(220, 225, 235);
      doc.setLineWidth(0.2);
      doc.line(margin, y - 3, margin + contentW, y - 3);
    }
  }

  // ── Page footers ──────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(ACCENT_R, ACCENT_G, ACCENT_B);
    doc.rect(0, pageH - 10, pageW, 10, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 195, 215);
    doc.text(`${companyLabel} — ${t('CONFIDENTIAL')}`, margin, pageH - 3.5);
    doc.text(t('Page {page} of {total}', { page: i, total: totalPages }), pageW - margin, pageH - 3.5, { align: 'right' });
  }

  const filename = `guardian-report-${report.id.slice(0, 8)}-${formatDateShort(report.created_at).replace(/\//g, '-')}.pdf`;
  doc.save(filename);
}

// ─── Word Export ──────────────────────────────────────────────────────────────

export async function exportReportToDocx(
  report: Report,
  comments: ReportComment[],
  media: ReportMedia[],
  includeInternalComments = false,
  companyName = '',
): Promise<void> {
  const profiles = report['profiles'] as { full_name?: string | null } | null;
  const sites = report['sites'] as { name?: string | null } | null;
  const assignee = report['assignee'] as { full_name?: string | null } | null;
  const title = getReportTitle(report);
  const reportType = reportTypeLabelFor(report.report_type);
  const sections = buildReportSections(report);

  const children: (Paragraph | Table)[] = [];

  // ── Title ─────────────────────────────────────────────────────────────────
  const docCompanyLabel = companyName || t('Security Report');
  children.push(
    new Paragraph({
      text: docCompanyLabel.toUpperCase(),
      heading: HeadingLevel.HEADING_3,
      style: 'Strong',
    }),
    new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: 40, color: '1E3A5F' })],
      spacing: { after: 100 },
    }),
    new Paragraph({
      children: [new TextRun({ text: reportType, size: 22, color: '4A6080' })],
      spacing: { after: 300 },
    }),
  );

  // ── Metadata table ────────────────────────────────────────────────────────
  const metaRows: [string, string][] = [
    [t('Guard'), profiles?.full_name || t('Unknown')],
    [t('Site'), sites?.name || t('Unknown')],
    [t('Shift'), getReportShiftLabel(report)],
    [t('Status'), getStatusLabel(report.status)],
    [t('Priority'), (report.priority || t('N/A')).toUpperCase()],
    [t('Assigned To'), assignee?.full_name || t('Unassigned')],
    [t('Report ID'), report.id],
    [t('Submitted'), formatDate(report.created_at)],
  ];
  if (report.status_updated_at && report.status_updated_at !== report.created_at) {
    metaRows.push([t('Last Updated'), formatDate(report.status_updated_at)]);
  }

  children.push(
    makeDocxTable(metaRows),
    new Paragraph({ text: '', spacing: { after: 300 } }),
  );

  // ── Report sections ───────────────────────────────────────────────────────
  for (const section of sections) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: section.title, bold: true, size: 26, color: '1E3A5F' })],
        spacing: { before: 300, after: 100 },
        border: { bottom: { color: '1E3A5F', size: 6, style: BorderStyle.SINGLE } },
      }),
    );

    if (section.facts.length > 0) {
      children.push(
        makeDocxTable(section.facts.map((f) => [f.label, f.value])),
        new Paragraph({ text: '', spacing: { after: 100 } }),
      );
    }

    if (section.body) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: section.body, size: 22 })],
          spacing: { after: 200 },
        }),
      );
    }
  }

  // ── Source content ────────────────────────────────────────────────────────
  if (report.content) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: sections.length > 0 ? t('Source Summary') : t('Report Content'), bold: true, size: 26, color: '1E3A5F' })],
        spacing: { before: 300, after: 100 },
        border: { bottom: { color: '1E3A5F', size: 6, style: BorderStyle.SINGLE } },
      }),
      new Paragraph({
        children: [new TextRun({ text: report.content, size: 22 })],
        spacing: { after: 300 },
      }),
    );
  }

  // ── Attachments ───────────────────────────────────────────────────────────
  if (media.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: t('Attachments ({count})', { count: media.length }), bold: true, size: 26, color: '1E3A5F' })],
        spacing: { before: 300, after: 200 },
        border: { bottom: { color: '1E3A5F', size: 6, style: BorderStyle.SINGLE } },
      }),
    );

    for (const m of media) {
      if (m.media_type === 'video') {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '🎬 ', size: 22 }),
              new TextRun({ text: t('Video attachment'), bold: true, size: 22 }),
              new TextRun({ text: ` (${t('view in Guardian portal')})`, size: 22, italics: true, color: '666666' }),
            ],
            spacing: { after: 120 },
          }),
        );
      } else if (m.url) {
        const buf = await fetchImageAsArrayBuffer(m.url);
        if (buf) {
          try {
            const mimeType = getImageType(m.url);
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: buf,
                    transformation: { width: 400, height: 250 },
                    type: mimeType === 'image/png' ? 'png' : 'jpg',
                  }),
                ],
                spacing: { after: 120 },
              }),
              new Paragraph({
                children: [new TextRun({ text: t('Photo attachment'), italics: true, size: 18, color: '888888' })],
                spacing: { after: 200 },
              }),
            );
          } catch {
            children.push(
              new Paragraph({
                children: [new TextRun({ text: t('Photo attachment (could not embed)'), italics: true, size: 20, color: '888888' })],
                spacing: { after: 120 },
              }),
            );
          }
        }
      }
    }
  }

  // ── Comments ──────────────────────────────────────────────────────────────
  const exportableComments = includeInternalComments
    ? comments
    : comments.filter((c) => !c.is_internal);

  if (exportableComments.length > 0) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: t('Comments ({count})', { count: exportableComments.length }), bold: true, size: 26, color: '1E3A5F' })],
        spacing: { before: 400, after: 200 },
        border: { bottom: { color: '1E3A5F', size: 6, style: BorderStyle.SINGLE } },
      }),
    );

    for (const comment of exportableComments) {
      const author = comment.profiles?.full_name || t('Unknown');
      const role = comment.profiles?.role === 'admin' ? ` (${t('Admin')})` : '';
      const internalTag = comment.is_internal ? ` [${t('Internal Note')}]` : '';

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${author}${role}${internalTag}`, bold: true, size: 22, color: comment.is_internal ? '92400E' : '1E3A5F' }),
            new TextRun({ text: `  ·  ${formatDate(comment.created_at)}`, size: 20, color: '888888' }),
          ],
          spacing: { before: 200, after: 80 },
        }),
        new Paragraph({
          children: [new TextRun({ text: comment.body, size: 22 })],
          shading: comment.is_internal
            ? { type: ShadingType.SOLID, color: 'FEF3C7', fill: 'FEF3C7' }
            : undefined,
          spacing: { after: 160 },
        }),
      );
    }
  }

  // ── Exported timestamp ────────────────────────────────────────────────────
  children.push(
    new Paragraph({ text: '', spacing: { before: 600 } }),
    new Paragraph({
      children: [new TextRun({ text: t('Exported from {company} portal on {date}', {
        company: docCompanyLabel,
        date: new Date().toLocaleString(getLocale()),
      }), italics: true, size: 18, color: '999999' })],
      alignment: AlignmentType.CENTER,
    }),
  );

  const doc = new Document({
    sections: [
      {
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: docCompanyLabel.toUpperCase(), bold: true, size: 18, color: '1E3A5F' }),
                  new TextRun({ text: `  |  ${t('Confidential Report')}`, size: 18, color: '888888' }),
                ],
                alignment: AlignmentType.LEFT,
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: `${docCompanyLabel}  |  ${t('Confidential')}  |  ${t('Page')} `, size: 18, color: '888888' }),
                  new TextRun({ children: [PageNumber.CURRENT], size: 18, color: '888888' }),
                  new TextRun({ text: ` ${t('of')} `, size: 18, color: '888888' }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 18, color: '888888' }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = `guardian-report-${report.id.slice(0, 8)}-${formatDateShort(report.created_at).replace(/\//g, '-')}.docx`;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Shared DOCX helper ────────────────────────────────────────────────────────

function makeDocxTable(rows: [string, string][]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map(
      ([label, value]) =>
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, size: 20, color: '2D4E7A' })] })],
              width: { size: 28, type: WidthType.PERCENTAGE },
              shading: { type: ShadingType.SOLID, color: 'EFF4FB', fill: 'EFF4FB' },
              margins: { top: 80, bottom: 80, left: 120, right: 80 },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: value, size: 20 })] })],
              width: { size: 72, type: WidthType.PERCENTAGE },
              margins: { top: 80, bottom: 80, left: 120, right: 80 },
            }),
          ],
        }),
    ),
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: 'D1DBE8' },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: 'D1DBE8' },
      left: { style: BorderStyle.SINGLE, size: 4, color: 'D1DBE8' },
      right: { style: BorderStyle.SINGLE, size: 4, color: 'D1DBE8' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'E8EEF5' },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: 'E8EEF5' },
    },
  });
}
